import { ReactNode, createContext, useContext, useEffect } from "react";
import { fetchMe } from "@services/http/user";
import { useQuery } from "@tanstack/react-query";
import { parseCookies } from "nookies";
import { UserType } from "@@types/IUser";
import { queryClient } from "@services/queryClient";

interface UserContextData {
  user: UserType | undefined;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
}

interface UserProviderProps {
  children: ReactNode;
}

const UserContext = createContext({} as UserContextData);

export function UserProvider({ children }: UserProviderProps) {
  const { "admin.token": token } = parseCookies();

  const {
    data: user,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(["/users", "/me", token], fetchMe, {
    enabled: !!token,
    staleTime: 15 * 60 * 1000, // 15 minutos
    cacheTime: 5 * 60 * 1000, // 5 minutos
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (token) {
      queryClient.invalidateQueries(["/users", "/me"]);
    }
  }, [token]);

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        isFetching,
        refetch,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
}
